<template>
  <div role="radiogroup" data-test-id="color-picker" :style="{ width: width }">
    <div v-for="(set, index) in colorSets" :key="set.name" data-test-id="color-picker-set">
      <div v-if="set.name" class="color-picker__set-name">{{ set.name }}</div>
      <div class="color-picker">
        <button
          v-for="color in set.colors"
          :key="color.rgb"
          :class="{ active: color.rgb === modelValue }"
          :aria-checked="color.rgb === modelValue"
          :aria-label="color.name"
          type="button"
          class="color-picker__button"
          role="radio"
          :data-test-color-hex="color.rgb"
          @click="($event) => $emit('update:modelValue', color.rgb, $event)"
          @keydown.enter.prevent="($event) => $emit('update:modelValue', color.rgb, $event)"
        >
          <CheckIcon16 class="color-picker__check" />

          <span class="color-circle" :style="{ 'background-color': color.rgb }" />
        </button>
        <button
          v-if="noColorAllowed && index == colorSets.length - 1"
          :title="$t('colorPicker.noColor')"
          :class="{ active: !modelValue }"
          type="button"
          class="color-picker__button"
          role="radio"
          @click="($event) => $emit('update:modelValue', '', $event)"
          @keydown.enter.prevent="($event) => $emit('update:modelValue', '', $event)"
          data-test-id="color-picker-colorless-button"
        >
          <CheckIcon16 v-if="!modelValue" class="color-picker__check color-picker__check--no-color" />

          <span class="color-circle color-circle--no-color" />
        </button>

        <ColorPaletteButton
          v-if="arbitraryColor && index == colorSets.length - 1"
          :model-value="modelValue"
          @change="($event) => $emit('update:modelValue', $event)"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';

import { Color, ColorSet } from '@/types';
import CheckIcon16 from '@components/Icons/CheckIcon16.vue';
import ColorPaletteButton from '@components/common/ColorPaletteButton.vue';

export default defineComponent({
  name: 'ColorPicker',

  emits: {
    'update:modelValue': null,
  },

  components: {
    CheckIcon16,
    ColorPaletteButton,
  },

  props: {
    colors: {
      type: Array as PropType<Array<Color>>,
      required: true,
    },

    extraColorSets: {
      type: Array as PropType<Array<ColorSet>>,
      default(): Array<ColorSet> {
        return [];
      },
    },

    modelValue: {
      type: String,
      required: false,
      default: null,
    },

    noColorAllowed: {
      type: Boolean,
      default: false,
    },

    arbitraryColor: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    colorSets(): Array<ColorSet> {
      return [
        {
          name: '',
          colors: this.colors,
        },
        ...this.extraColorSets,
      ];
    },

    // can this be done with css?
    width() {
      const setcolors = this.colorSets.reduce((acc, set) => acc + set.colors.length, 0);
      const total = setcolors + this.colors.length + 1; // +1 is for the picker
      const cols = Math.min(total, 7); // max 7 cols
      const width = cols * 40 + (cols - 1) * 4; // uses width and gap

      return `${width}px`;
    },
  },
});
</script>

<style lang="scss">
@use 'settings/colors';
@use 'settings/settings';
.color-picker__set-name {
  padding: 0 16px;
}

.color-picker {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.color-picker__button {
  position: relative;
  border: 1px solid transparent;
  width: 40px;
  height: 40px;
  border-radius: settings.$border-radius;
  transition: all 0.1s ease-in-out;
  display: flex;
  align-items: center;
  justify-content: center;

  &.active,
  &:hover {
    border: 1px solid colors.$color-grey-300;
  }

  &.active .color-picker__check {
    opacity: 1;
  }
}

.color-picker__check {
  position: absolute;
  top: calc(50% - 8px);
  left: calc(50% - 8px);
  opacity: 0;
  transition: all 0.1s ease-in-out;
  color: colors.$color-white;
}

.color-picker__check--no-color {
  color: colors.$color-grey-700;
}

.color-circle {
  width: 24px;
  height: 24px;
  border-radius: 50%;
}

.color-circle--no-color {
  --checkerSize: 6px;
  --checkerColor: #ffffff;
  --checkerAltColor: #e5e5e5;

  background-image: linear-gradient(45deg, var(--checkerColor) 25%, transparent 25%),
    linear-gradient(135deg, var(--checkerColor) 25%, transparent 25%),
    linear-gradient(45deg, transparent 75%, var(--checkerColor) 75%),
    linear-gradient(135deg, transparent 75%, var(--checkerColor) 75%);
  background-color: var(--checkerAltColor);

  background-size: calc(2 * var(--checkerSize)) calc(2 * var(--checkerSize));

  background-position: 0 0, var(--checkerSize) 0, var(--checkerSize) calc(-1 * var(--checkerSize)),
    0px var(--checkerSize);

  box-shadow: settings.$box-shadow-border-grey-80;
}
</style>
